import {Box, Radio} from '@yandex-delivery/blocks'
import React, {useCallback, useMemo, useState} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import type {OrderFormState} from '../../types'
import {useAction} from '../../actions'
import {ActionComponentProps} from '../../actions/types'
import {useFormUserEventHandler} from '../../lib/metrika'
import {WidgetPropsByType, WidgetType} from '../types'

export const RadioWidget = ({
  action,
  enabled,
  metrica_label,
  name,
  value: externalValue,
}: WidgetPropsByType<WidgetType.RADIO>): React.ReactElement => {
  const {control} = useFormContext<OrderFormState>()
  const {
    field: {value: fieldValue, onChange, ...field},
  } = useController({
    name,
    control,
  })

  const handleReachGoal = useFormUserEventHandler()

  const [innerValue, setInnerValue] = useState(externalValue)

  const handleActionChange = useCallback(
    (val: string) => {
      onChange(val)
      setInnerValue(val)
    },
    [onChange],
  )

  const actionProps = useMemo<Pick<ActionComponentProps, 'onChange'>>(
    () => ({
      onChange: handleActionChange,
    }),
    [handleActionChange],
  )

  const {ActionComponent, triggerAction} = useAction(action, actionProps)

  const handleClick = useCallback(() => {
    handleReachGoal(metrica_label)

    if (triggerAction) {
      triggerAction()
      return
    }

    onChange(externalValue)
  }, [externalValue, handleReachGoal, metrica_label, onChange, triggerAction])

  return (
    <Box>
      <Radio
        {...field}
        checked={(fieldValue !== void 0 && fieldValue === innerValue) || fieldValue === externalValue}
        disabled={!enabled}
        onClick={handleClick}
        value={innerValue || externalValue}
      />

      {ActionComponent}
    </Box>
  )
}
